/**
 * {@overview}
 * Here we have the specific ways to process highlighters in a fast way
 * using Canvas and other tricks
 *
 * We use the class `highlighter--scrollcontainer` {@link contextScrollContainerClass} to catch the container of the selection menu
 * and draggy bubble things.
 */

import { HighlightCanvas, HighlightRectsInstance, PopCanvas, SelectionCanvas } from "./canvas"
import { derived, readable, type Readable, type Unsubscriber } from "svelte/store"
import { fontsReady } from "@utils/helpers/fonts-ready"
import { awaitStore } from "@utils/helpers/await-store"
import { LineHelpers } from "./line-helpers"
import { MultiTap, SelectionLogic, type SelectionLogicEventMap } from "./selection-logic"
import { WordBoxRange } from "./wordbox"
import { AnimationFramePender, IdleCallbackPender } from "@utils/helpers/penders"
import { WordReference, weigh } from "@circleofa/acim-engine"
import {
    SelectionType,
    globalSelectHandler,
    type GlobalSelectionEmitterEventHandlerMap,
    Grabber,
    GrabberState,
} from "./global-select-handler"
import { acim } from "@utils/acim"
import {
    Highlight,
    findHighlight,
    type HighlightID,
    getClassForHighlighter,
} from "@store/highlights"
import { PinnedDragger } from "./dragger-logic"
import { EventWrapper } from "@utils/helpers/event-wrapper"
import { CustomEventDispatcher } from "@utils/helpers/event-helper"
import { HighlightSubtree } from "@store/highlight-subtree"
import { TableRefresh } from "./table-refresh"
import { easyFontPreference, fontSize } from "@store/deviceSettings"
import {
    HighlightSettingLimiter,
    type HighlighterListenerSettings,
    HighlighterListenerSettingsDefault,
    FocusSetting,
} from "./highlight-listener-permissions"
import { ScrollLock, scrollLockClass } from "./scroll-locker"
import { skipFirstCall } from "@utils/helpers/skip-first"
import { Focusser } from "./focusser"
import { getHighlightCanvasBoundsKey, getHighlightGeometryKey } from "./layout-geometry"

/**
 * Scrollable container class - this isn't the actual scrollable element
 * but rather the thing that scrolls within it.
 */
export const contextScrollContainerClass = "highlighter--scrollcontainer"

/**
 * This informs the highlight directive that the container is resizing
 * and we should refresh the pages
 */
export class RedrawEventDispatcher extends CustomEventDispatcher<{ redraw: string }> {
    #size = 0
    constructor() {
        super()
    }

    resize(size: number) {
        if (size === this.#size) return
        this.#size = size
        this.dispatch("redraw", "" + size)
    }
}

/**
 * We only want to render boxes are the fonts are loaded, otherwise
 * the clientRects of the words are skewed.
 */
const boxesReady = derived(fontsReady, (fontsReady) => fontsReady)

/**
 * Should be attached to a 'reader' object
 * @param node
 * @param options
 * @global needs context for words
 */
export function attachHighlighterListener(
    node: HTMLElement,
    {
        redrawEventDispatcher,
        settings = {},
        disabled = false,
        waitUntil = readable(true),
    }: {
        redrawEventDispatcher?: RedrawEventDispatcher
        settings?: Partial<HighlighterListenerSettings>
        disabled?: boolean
        /**
         * Sometimes it's important to wait until some animation is finished before loading
         */
        waitUntil?: Readable<boolean>
    },
) {
    const newSettings = { ...HighlighterListenerSettingsDefault, ...settings }
    let highlighterListener: HighlighterListener | null = null
    const idleCallback = new IdleCallbackPender()

    let cancelled = false

    if (!disabled)
        awaitStore(boxesReady).then(() => {
            awaitStore(waitUntil).then(async () => {
                if (cancelled) return
                // console.log({node})
                idleCallback.overwrite(() => {
                    highlighterListener = new HighlighterListener(node, newSettings)
                })
            })
        })

    const redrawFn = ({ detail: key }: CustomEvent<string>) => {
        highlighterListener?.refresh(key)
    }

    redrawEventDispatcher?.addEventListener("redraw", redrawFn)

    return {
        destroy() {
            cancelled = true
            idleCallback.cancel()
            redrawEventDispatcher?.removeEventListener("redraw", redrawFn)
            highlighterListener?.destroy()
        },
    }
}

type HighlighterSelection = {
    type: SelectionType
} & (
    | {
          type: SelectionType.HIGHLIGHT
          highlightID: HighlightID
      }
    | {
          type: SelectionType.TEXT
          range: WordBoxRange | null
      }
)

/**
 *
 */
export class HighlighterListener {
    /**
     * This stores a portion of the highlights for reactivity and debouncing before
     * committing to the database. {@link HighlightSubtree} for more.
     */
    #highlightSubtree: HighlightSubtree | null = null

    /**
     * LineHelpers helps us to organise the lines, wordboxes, and wordrects with
     * some helper APIs
     */
    #lineHelpers: LineHelpers

    /**
     * This is the element that the canvas attaches to. It's the offsetParent
     */
    #parent: HTMLElement

    /**
     * This is where the selection dialogue componentattaches to. It should be in
     * the same scrollable layer as the text itself, so that when you scroll up
     * and down it follows. It finds {@link contextScrollContainerClass} up
     * the hierarchty of nodes.
     */
    #contextMenuContainer: HTMLElement

    /**
     * Just a reference for idle callbacks
     */
    #generateRectsPender = new IdleCallbackPender()

    /**
     * Responsible for distilling clicks, double-clisk, drags, etc into meaningful
     * events that we can use
     */
    #selectionLogic: SelectionLogic | null = null
    #selectionLogicEvents: EventWrapper<SelectionLogicEventMap, SelectionLogic> | null = null
    #globalSelectEvents: EventWrapper<
        GlobalSelectionEmitterEventHandlerMap,
        typeof globalSelectHandler
    > | null = null

    #tableRefresh: TableRefresh | null = null

    /**
     * Holds the data of the currently selected text or highlight.
     * If the highlight is selected, the data comes from the outside -
     * {@link #highlightSubtree}. If it's a text selection, it comes from
     * {@link #selectionLogic} via pointer events.
     *
     * The idea with this is that it should always be synced with
     * {@link globalSelectHandler}.
     */
    #selection: HighlighterSelection | null = null

    /**
     * Keeps the selection dragging in an animation frame
     * pender so that it doesn't slow down the app
     * when you're doing it quickly
     */
    #selectionDragPender = new AnimationFramePender()

    /**
     * Keeps the highlight drawing in an animation frame
     * pender so that it doesn't slow down the app
     * when you're doing it quickly
     */
    #highlightRedrawPender = new AnimationFramePender()

    /**
     * Deals with drawing the selection boxes
     */
    #selectionCanvas: SelectionCanvas | null = null

    /**
     * Helps to modulate resizing redrawing. Only redraws
     * when it's rested.
     */
    #refreshPender = new IdleCallbackPender()

    /**
     * Holds the data for which dragger is currently pinned
     */
    #pinnedDragger: PinnedDragger | null = null

    /**
     * Handling the actual highlight drawing
     */
    #highlightCanvas: HighlightCanvas | null = null

    /**
     * Pop
     */
    #popWordCanvas: PopCanvas | null = null

    /**
     * Font change size listener
     */
    #fontSizeUnsubscribe: Unsubscriber | null = null

    /**
     * Easy-font swaps the reader font family without changing the font-size store.
     */
    #easyFontUnsubscribe: Unsubscriber | null = null

    /**
     * Catches geometry changes that do not flow through a shared
     * RedrawEventDispatcher. Highlight rects are measured relative to
     * #parent, so both the text box and its offset inside #parent matter.
     */
    #layoutResizeObserver: ResizeObserver | null = null
    #layoutGeometryKey: string | null = null
    #layoutCanvasBoundsKey: string | null = null

    /**
     * Container responsible for scrolling
     */
    #scrollLock: ScrollLock

    /**
     * Responsible for focussing on a certain highlight
     * or search result by compressing the visible
     * area
     */
    #squinter: Focusser | null = null

    constructor(
        public reader: HTMLElement,
        private readonly listenerSettings: HighlighterListenerSettings,
    ) {
        const parent = <HTMLElement | null>reader.offsetParent
        if (!parent) throw new Error("Parent not set for reader highlighter initiation")

        this.#parent = parent

        // Context menu parent is what's used to hook the context menu into.
        // In certain contexts this is shared with a lot of other reader elements
        // Like in highlights and search lists
        const contextMenuParent = <HTMLElement>reader.closest(`.${contextScrollContainerClass}`)
        if (!contextMenuParent)
            throw new Error(
                `Context menu container not found. No parent found with class: ${contextScrollContainerClass}`,
            )
        this.#contextMenuContainer = contextMenuParent

        // Scrollable Parent
        // this is the "body" of the whole text, *not* the scrollable container
        const scrollLockParent = <HTMLElement>reader.closest(`.${scrollLockClass}`)
        if (!scrollLockParent)
            throw new Error(
                `Scrollable container not found. No parent found with class: ${scrollLockClass}`,
            )
        this.#scrollLock = new ScrollLock(scrollLockParent)

        //// --- INITIALISATION LINES

        // Get all the lines
        this.#lineHelpers = new LineHelpers(
            [...reader.querySelectorAll<HTMLSpanElement>("span.reader--line")],
            parent,
        )

        //// --- INITIALISATION HIGHLIGHTS

        // Setup highlight canvas - this should go first for z-index purposes
        this.#highlightCanvas = new HighlightCanvas(parent)

        // Set up reactivity to highlight subtree
        this.#highlightSubtree = new HighlightSubtree(
            new WordReference({
                displayWord: this.#lineHelpers.firstWord,
                acim,
            }),
            new WordReference({
                displayWord: this.#lineHelpers.lastWord,
                acim,
            }),
            (highlights) => this.onHighlightChange(highlights),
        )

        //// --- POP INITIALISATION

        /**
         * This is for emphasizing words like in search results
         */
        if (listenerSettings.popWords !== null) {
            this.#popWordCanvas = new PopCanvas(parent)
        }

        //// --- SELECTION INITIALISATION

        // SelectionCanvas handles the drawing of the selection
        this.#selectionCanvas = new SelectionCanvas(parent)
        this.#selectionCanvas.show()

        // Initialise Selection Logic - handling clicks and such
        this.#selectionLogic = new SelectionLogic(parent, this.#scrollLock)

        // Handle Selection Logic Events
        this.#selectionLogicEvents = new EventWrapper(this.#selectionLogic)
        this.#selectionLogicEvents.register({
            dragstart: ({ detail }) => {
                if (!this.#lineHelpers)
                    throw new Error(
                        "Line helpers is unset, and we were expecting it to not be set because",
                    )
                const closestWord = this.#lineHelpers.closestWord(detail.x, detail.y)
                console.log({ closestWord })
                if (closestWord) {
                    this.#pinnedDragger = PinnedDragger.fromWordBox(closestWord)
                    this.#handleDeselect()
                    this.#fireSelectEvent()
                    this.#selection = {
                        type: SelectionType.TEXT,
                        range: this.#pinnedDragger.getRange(),
                    }
                    this.#processRangeHighlight()
                }
            },
            dragmove: ({ detail }) => {
                const closestWord = this.#lineHelpers.closestWord(detail.x, detail.y)
                if (closestWord) {
                    if (this.#pinnedDragger === null)
                        throw new Error(
                            "A drag move was detected but #pinnedDragger was null. Expected an initialiser.",
                        )
                    this.#pinnedDragger.updateBox(closestWord)
                    if (this.#selection === null)
                        throw new Error("A drag move was detected but #selection was null.")
                    if (this.#selection.type === SelectionType.HIGHLIGHT)
                        throw new Error(
                            "A drag move event was passed to the highlight listener but the selection type was a highlight, not text.",
                        )
                    this.#selection.range = this.#pinnedDragger.getRange()
                    this.#processRangeHighlight()
                }
            },
            tap: ({ detail: { x, y, tap } }) => {
                if (tap === MultiTap.SINGLE) {
                    const previouslySelectedHighlight = this.#getSelectedHighlightID()

                    // Deselect current
                    this.#handleDeselect()
                    globalSelectHandler.cancelAll()

                    // Bail if disabling highlighter editing
                    if (this.listenerSettings.editHighlights === HighlightSettingLimiter.NONE)
                        return

                    if (this.#highlightCanvas === null)
                        throw new Error(
                            "Highlight canvas was not set during tap. This should only happen if editHighlights setting is set to none.",
                        )

                    // Get list of highlights at x,y point
                    const highlightsIDList = this.#highlightCanvas.testHitboxPoint(x, y)

                    if (
                        this.listenerSettings.editHighlights === HighlightSettingLimiter.EMPHASIZED
                    ) {
                        if (this.listenerSettings.emphasizeHighlight === null)
                            throw new Error(
                                "Attempt to check if emphasized highlighter was in list but emphasized highlighter not set.",
                            )
                        if (
                            this.listenerSettings.emphasizeHighlight !== "faded" &&
                            highlightsIDList.includes(this.listenerSettings.emphasizeHighlight)
                        ) {
                            return this.selectHighlight(
                                this.listenerSettings.emphasizeHighlight,
                                GrabberState.STATIC,
                            )
                        }
                    } else {
                        // HighlightSettingLimiter === ALL
                        if (highlightsIDList.length === 1)
                            return this.selectHighlight(highlightsIDList[0], GrabberState.STATIC)
                        if (highlightsIDList.length > 1) {
                            if (previouslySelectedHighlight) {
                                const currentIndex = highlightsIDList.findIndex(
                                    (id) => id === previouslySelectedHighlight,
                                )
                                const nextID = (currentIndex + 1) % highlightsIDList.length
                                return this.selectHighlight(
                                    highlightsIDList[nextID],
                                    GrabberState.STATIC,
                                )
                            } else {
                                return this.selectHighlight(
                                    highlightsIDList[0],
                                    GrabberState.STATIC,
                                )
                            }
                        }
                    }

                    // SelectHighlight calls this automatically, so if we're not setting a highlight, we should call it
                    this.#processRangeHighlight()
                } else {
                    const closestPoint = this.#lineHelpers.closestWord(x, y)
                    // Must be a single word
                    if (!closestPoint.next) return null
                    if (closestPoint.next !== closestPoint.prev) return null
                    const closestWord = closestPoint.next
                    if (tap === MultiTap.DOUBLE) {
                        const range = WordBoxRange.SentenceRangeFromWord(closestWord)
                        this.#fireSelectEvent()
                        this.#selection = {
                            type: SelectionType.TEXT,
                            range,
                        }
                        this.#processRangeHighlight()
                    } else if (tap === MultiTap.TRIPLE) {
                        const range = WordBoxRange.ParagraphRangeFromWord(closestWord)
                        this.#fireSelectEvent()
                        this.#selection = {
                            type: SelectionType.TEXT,
                            range,
                        }
                        this.#processRangeHighlight()
                    }
                }
            },
            dragend: ({ detail }) => {
                const closestWord = this.#lineHelpers.closestWord(detail.x, detail.y)
                if (closestWord) {
                    this.#pinnedDragger = null
                    this.#processRangeHighlight()
                }
            },
        })

        //// --- FOCUS INITIATISATION

        this.#initialiseFocus()

        //// --- GLOBAL EVENT LISTENERS

        // Setup listeners to global selection events
        this.#globalSelectEvents = new EventWrapper(globalSelectHandler)
        this.#globalSelectEvents.register({
            // @ts-expect-error not sure why
            deselect: ({ detail }) => {
                if (detail.preserveOneSelection === true && detail.target === this) return
                this.#handleDeselect()
                if (this.#selectionCanvas !== null) this.#selectionCanvas.clearSelection()
            },
            // @ts-expect-error not sure why
            select: ({ detail }) => {
                if (detail.selection.highlighterListener !== this) {
                    if (this.#selection !== null) {
                        this.#handleDeselect()
                        if (this.#selectionCanvas !== null) this.#selectionCanvas.clearSelection()
                    }
                }
            },
        })

        //// --- ETC

        // Initialise table listener
        this.#tableRefresh = new TableRefresh(reader, () => this.refresh())

        // Setup font size listener
        this.#fontSizeUnsubscribe = fontSize.subscribe(skipFirstCall(() => this.refresh()))
        this.#easyFontUnsubscribe = easyFontPreference.subscribe(
            skipFirstCall(() => this.refresh()),
        )

        this.#initialiseLayoutObserver()

        //// --- GENERATE

        // Call the first measurements of words
        this.refresh()
    }

    /**
     * This should be called whenever there is a change
     * For example when the {@link reader} is resized,
     * or when the tables within the reader are scrolled
     * via {@link TableRefresh}
     * @param key key to be supplied for the squiter to only
     * invalidate those who's widths have changed. If it isn't
     * supplied, we will skip invalidation of the focusser.
     */
    refresh(key?: string) {
        this.listenerSettings.callbacks.loading?.()
        if (this.#selectionCanvas !== null) {
            this.#selectionCanvas.hide()
            this.#selectionCanvas.resizeCanvas()
        }
        if (this.#highlightCanvas !== null) {
            this.#highlightCanvas.hide()
            this.#highlightCanvas.resizeCanvas()
        }
        if (this.#popWordCanvas !== null) {
            this.#popWordCanvas.hide()
            this.#popWordCanvas.resizeCanvas()
        }

        if (this.#squinter !== null) {
            if (key !== undefined) {
                console.log("Invalidating! with ", key)
                this.#squinter.invalidateIfNew(key)
            } else {
                console.log("Forcing invalidation ")
                this.#squinter.invalidate()
            }
        }

        this.#refreshPender.overwrite(() => {
            this.generateClientRectsNow()
            this.#updateSelectionRange()
            this.#processRangeHighlight()
            this.#updatePopWords()
            this.listenerSettings.callbacks.painted?.()
        })

        if (this.listenerSettings.forceInstantFirstPaint === true) {
            this.#refreshPender.run()
        }
    }

    /**
     * Updates the emphasized words
     */
    #updatePopWords() {
        if (this.#popWordCanvas === null) return
        if (this.listenerSettings.popWords === null)
            throw new Error("popWordCanvas is set but there are no popWords")

        const ranges = this.listenerSettings.popWords.map(([start, end]) => {
            return this.#referencesToWordBoxRange(
                new WordReference({
                    displayWord: start,
                    acim,
                }),
                new WordReference({
                    displayWord: end,
                    acim,
                }),
            )
        })

        this.#popWordCanvas.drawSelectedWords(ranges.flatMap((range) => range.getOffsetRects()))
    }

    /**
     * Invalidates the current selection with the new range
     */
    #updateSelectionRange() {
        if (this.#selection === null) return
        if (this.#selection.type !== SelectionType.TEXT) return
        if (this.#selection.range === null) return
        const newRange = this.#referencesToWordBoxRange(
            new WordReference({
                displayWord: this.#selection.range.start.word,
                acim,
            }),
            new WordReference({
                displayWord: this.#selection.range.end.word,
                acim,
            }),
        )
        this.#selection.range = newRange
    }

    /**
     * This should be purely visual and only send messages
     * to the {@link globalSelectHandler}. It should not change
     * the state of this HighlighterListener
     */
    #processRangeHighlight() {
        this.#selectionDragPender.overwrite(() => {
            if (this.#selection === null) {
                return
            }
            if (this.#selection.type === SelectionType.TEXT) {
                if (this.listenerSettings.selection === true) {
                    if (this.#selectionCanvas === null)
                        throw new Error(
                            "Selection was set to true but selection canvas was not set.",
                        )
                    const wordBoxRange = this.#selection.range
                    if (wordBoxRange === null) return
                    const rects = wordBoxRange.getOffsetRects()
                    this.#selectionCanvas.drawSelection(rects)

                    // Apply to universal global selection
                    globalSelectHandler.registerSelection(
                        new WordReference({
                            displayWord: wordBoxRange.start.word,
                            acim: acim,
                        }),
                        new WordReference({
                            displayWord: wordBoxRange.end.word,
                            acim: acim,
                        }),
                        this.#parent,
                        this.#contextMenuContainer,
                        this,
                        wordBoxRange.topMostTop,
                        wordBoxRange.getSelectionBars(),
                        this.#pinnedDragger === null ? GrabberState.STATIC : GrabberState.MOVING,
                        this.listenerSettings,
                        this.#scrollLock,
                    )
                }
            }
        })
    }

    #handleDeselect() {
        if (this.#selection !== null) {
            this.#selection = null
            this.listenerSettings.callbacks.deselected?.()
        }
        this.#redrawHighlights()
    }

    /**
     * Call this BEFORE the selection changes,
     * so we don't fire a million events per minute
     * by checking to see if {@link this.selection} is null
     */
    #fireSelectEvent() {
        if (this.#selection === null) {
            this.listenerSettings.callbacks.selected?.()
        }
    }

    /**
     * -------------------------------------------------------------------------
     * ☑ Client Rect Generation
     * -------------------------------------------------------------------------
     */

    generateClientRects() {
        this.#generateRectsPender.overwrite(() => {
            this.generateClientRectsNow()
        })
    }

    generateClientRectsNow() {
        // const st = performance.now()
        const { left, top } = this.#parent.getBoundingClientRect()
        this.#lineHelpers.generateBoxes(left, top)
        // console.log("Client rects generated in", performance.now() - st)
        // Regenerate highlights
        if (this.listenerSettings.showHighlights === HighlightSettingLimiter.NONE) return
        if (this.#highlightSubtree === null)
            throw new Error(
                "Highlight subtree was set to null, but the showHighlights was not set to none",
            )
        this.onHighlightChange(this.#highlightSubtree.getCurrentState())
    }

    /**
     * -------------------------------------------------------------------------
     * 👂 Highlight Handling
     * -------------------------------------------------------------------------
     */

    /**
     * Listens to changes from the highlight subtree ({@link subscribeHighlightSubtree})
     */
    onHighlightChange(highlights: IterableIterator<Highlight>) {
        if (!this.#lineHelpers.wordBoxes.length) {
            return
        }

        if (this.#highlightCanvas === null)
            throw new Error(
                "Highlight canvas was set to null, but onHighlight changed should never have been called.",
            )

        this.#highlightCanvas.removeAllHighlights()
        for (const highlight of highlights) {
            const range = this.#referencesToWordBoxRange(
                highlight.start,
                highlight.end,
            ).getOffsetRects()
            this.#highlightCanvas.setHighlight(
                new HighlightRectsInstance(
                    highlight.id,
                    getClassForHighlighter(highlight.getHighlighter().id),
                    range,
                    highlight.hasNote,
                ),
                false,
            )
        }
        this.#redrawHighlights()
    }

    #redrawHighlights() {
        // Outline selected highlight
        this.#highlightRedrawPender.overwrite(() => {
            const highlightID = this.#getSelectedHighlightID()
            if (this.#highlightCanvas !== null)
                this.#highlightCanvas.redrawHighlights({
                    focussed: highlightID,
                    emphasize: this.listenerSettings.emphasizeHighlight,
                })
        })
    }

    #getSelectedHighlightID() {
        return this.#selection !== null && this.#selection.type === SelectionType.HIGHLIGHT
            ? this.#selection.highlightID
            : null
    }

    #initialiseLayoutObserver() {
        if (!("ResizeObserver" in window)) return

        const initialLayout = this.#measureLayout()
        this.#layoutGeometryKey = initialLayout.geometryKey
        this.#layoutCanvasBoundsKey = initialLayout.canvasBoundsKey
        this.#layoutResizeObserver = new ResizeObserver(() => {
            const layout = this.#measureLayout()
            const geometryChanged = layout.geometryKey !== this.#layoutGeometryKey
            const canvasBoundsChanged = layout.canvasBoundsKey !== this.#layoutCanvasBoundsKey

            this.#layoutGeometryKey = layout.geometryKey
            this.#layoutCanvasBoundsKey = layout.canvasBoundsKey

            if (geometryChanged) {
                this.refresh(layout.geometryKey)
            } else if (canvasBoundsChanged) {
                this.#resizeCanvases()
            }
        })
        this.#layoutResizeObserver.observe(this.reader)
        this.#layoutResizeObserver.observe(this.#parent)
    }

    #measureLayout() {
        const parentRect = this.#parent.getBoundingClientRect()
        const readerRect = this.reader.getBoundingClientRect()
        return {
            geometryKey: getHighlightGeometryKey(parentRect, readerRect),
            canvasBoundsKey: getHighlightCanvasBoundsKey(parentRect),
        }
    }

    #resizeCanvases() {
        this.#selectionCanvas?.resizeCanvas()
        this.#highlightCanvas?.resizeCanvas()
        this.#popWordCanvas?.resizeCanvas()
    }

    /**
     * This should be called from {@link globalSelectHandler} when we're
     * wanting to transfer a text selection to a highlight but keep the
     * selection. Assumes the highlight has already been created.
     */
    transferTextToHighlight(highlightID: HighlightID) {
        this.selectHighlight(highlightID, GrabberState.STATIC)
    }

    /**
     * Called by the `tap` event in the selection handler
     */
    selectHighlight(id: HighlightID, grabberState: GrabberState) {
        const highlight = findHighlight(id)
        if (highlight === null) throw new Error(`Highlight ${id} not found`)

        const wordBoxRange = this.#referencesToWordBoxRange(highlight.start, highlight.end)

        this.#fireSelectEvent()
        this.#selection = {
            type: SelectionType.HIGHLIGHT,
            highlightID: id,
        }

        globalSelectHandler.registerHighlight(
            id,
            highlight.start,
            highlight.end,
            this.#parent,
            this.#contextMenuContainer,
            this,
            wordBoxRange.topMostTop,
            wordBoxRange.getSelectionBars(),
            grabberState,
            this.listenerSettings,
            this.#scrollLock,
        )
        this.#processRangeHighlight()
        this.#redrawHighlights()
    }

    /**
     * -------------------------------------------------------------------------
     * 𖣣 Dragger Pin Handlers
     * -------------------------------------------------------------------------
     */

    /**
     * Should be called from the {@link globalSelectHandler}. This lets us know
     * the user has started to drag on one of the draggers
     */
    grabberDragStart(grabber: Grabber) {
        if (this.#pinnedDragger !== null) {
            // Should never happen
            throw new Error(
                "Attempt to drag via grabber except #pinnedDragger was already set (this means it should have been unset somewhere else but wasn't)",
            )
        }
        if (this.#selection === null)
            throw new Error("Highlighter selection not set on attempt to begin grabber drag")

        if (this.#selection.type === SelectionType.TEXT) {
            // Selection is text selection
            if (this.#selection.range === null) throw new Error("Selection range not set (new)")
            this.#pinnedDragger = PinnedDragger.fromRange(this.#selection.range, grabber)
        } else {
            // Selection is a highlight
            const highlight = findHighlight(this.#selection.highlightID)
            if (highlight === null) {
                this.#handleDeselect()

                // The highlight wasn't found, it's possible that it was deleted!
                // TODO: Handle this in the listener
                globalSelectHandler.cancelAll()
                throw new Error("Highlight not found during dragging initialisation!")
            }
            const range = this.#referencesToWordBoxRange(highlight.start, highlight.end)
            this.#pinnedDragger = PinnedDragger.fromRange(range, grabber)
        }
    }

    /**
     * Should be called from the {@link globalSelectHandler}. Should be triggered
     * when the user drags and grabber dragger.
     */
    grabberDragMove(x: number, y: number) {
        const closestWord = this.#lineHelpers.closestWord(x, y)
        if (closestWord === null)
            throw new Error(`Closest word not found during trigger move for x: ${x} and y: ${y}`)
        if (this.#pinnedDragger === null)
            throw new Error(`Pinned dragger not set during trigger move`)
        if (this.#selection === null)
            throw new Error("Highlighter selection was null during a dragger move")
        this.#pinnedDragger.updateBox(closestWord)
        const range = this.#pinnedDragger.getRange()

        if (this.#selection.type === SelectionType.TEXT) {
            this.#fireSelectEvent()
            this.#selection = {
                type: SelectionType.TEXT,
                range,
            }

            this.#processRangeHighlight()
        } else {
            // Selection type is HIGHLIGHT
            if (this.#highlightSubtree === null)
                throw new Error(
                    "Highlight subtree was not set (should only be set when highlights are selectable), but selection type was set to highlight.",
                )

            const highlight = findHighlight(this.#selection.highlightID)
            if (highlight === null) {
                throw new Error(
                    `Highlight not found during grabber drag move: ${this.#selection.highlightID}`,
                )
            }

            if (range === null) throw new Error("Range not set (new)")
            this.#highlightSubtree.changeRange(
                highlight.id,
                range.start.toWordReference(),
                range.end.toWordReference(),
            )
            this.selectHighlight(highlight.id, GrabberState.MOVING)
        }
    }

    /**
     * Should be called from the {@link globalSelectHandler}. Should be triggered
     * when the user finishes a drag.
     */
    grabberDragEnd() {
        this.#pinnedDragger = null
        if (this.#selection === null)
            throw new Error("Highlighter selection was null during a dragger move")
        if (this.#selection.type === SelectionType.TEXT) {
            this.#processRangeHighlight()
        } else {
            // Selection type is HIGHLIGHT
            if (this.#highlightSubtree === null)
                throw new Error(
                    "Highlight subtree was not set (should only be set when highlights are selectable), but selection type was set to highlight.",
                )
            this.#highlightSubtree.commit(this.#selection.highlightID)
            this.selectHighlight(this.#selection.highlightID, GrabberState.STATIC)
        }
    }

    /**
     * -------------------------------------------------------------------------
     * 🔧 Utilities
     * -------------------------------------------------------------------------
     */

    /**
     * Converts {@link WordReference}s to {@link WordBoxRange} for rendering
     * purposes.
     */
    #referencesToWordBoxRange(start: WordReference, end: WordReference): WordBoxRange {
        if (this.#lineHelpers.wordBoxes.length === 0) {
            throw new Error("wordBoxes have no been generated. Cannot find wordBoxes.")
        }

        const startDisplayWord = start.displayWord
        const endDisplayWord = end.displayWord

        const boxes = this.#lineHelpers.wordBoxes
        const startIndex = boxes.findIndex((wordBox) => startDisplayWord === wordBox.word)
        if (startIndex === -1) throw new Error(`WordBox start not found: ${startDisplayWord.path}`)
        const endIndex = boxes.findIndex((wordBox) => endDisplayWord === wordBox.word)
        if (endIndex === -1) throw new Error(`WordBox end not found: ${startDisplayWord.path}`)

        return new WordBoxRange(boxes[startIndex], boxes[endIndex])
    }

    /**
     * -------------------------------------------------------------------------
     * 🗑️ Init helpers
     * -------------------------------------------------------------------------
     */

    #initialiseFocus() {
        if (this.listenerSettings.focusOn !== null) {
            if (this.listenerSettings.squintEventEmitter === null)
                throw new Error("focusOn was set, but no event emitter was passed")

            // Initialiser focusser
            if (this.listenerSettings.focusOn == FocusSetting.HIGHLIGHT) {
                if (
                    this.listenerSettings.emphasizeHighlight === null ||
                    this.listenerSettings.emphasizeHighlight === "faded"
                ) {
                    throw new Error(
                        "focusOn was set to highlight, but the emphasizeHighlight was either null or faded",
                    )
                }
                const highlight = findHighlight(this.listenerSettings.emphasizeHighlight)
                if (highlight === null)
                    throw new Error(
                        `Highlightn not found for focusser! ${this.listenerSettings.emphasizeHighlight}`,
                    )
                this.#squinter = new Focusser(
                    this.reader,
                    this.listenerSettings.squintEventEmitter,
                    highlight.start.displayWord,
                    highlight.end.displayWord,
                )
            } else {
                if (this.listenerSettings.popWords === null) {
                    throw new Error("focusOn was set to words, but popWords were not set")
                }
                if (this.listenerSettings.popWords.length === 0) {
                    return
                    throw new Error("Popwords had no words, nothing to focus on!")
                }
                const allWords = this.listenerSettings.popWords.flatMap((r) => r)
                const sorted = allWords.sort((a, b) => weigh(a) - weigh(b))
                this.#squinter = new Focusser(
                    this.reader,
                    this.listenerSettings.squintEventEmitter,
                    sorted[0],
                    sorted[sorted.length - 1],
                )
            }

            // Apply the events if there is a
            if (this.listenerSettings.focusExpander !== null) {
                this.listenerSettings.focusExpander.addEventListener("expand", () =>
                    this.#squinter?.expand(),
                )
                this.listenerSettings.focusExpander.addEventListener("contract", () =>
                    this.#squinter?.contract(),
                )
            }
        }
    }

    /**
     * -------------------------------------------------------------------------
     * 🗑️ Unloading / Removing Listeners
     * -------------------------------------------------------------------------
     */

    destroy() {
        // Deselect if selected
        globalSelectHandler.cancel(this)

        this.#generateRectsPender.cancel()
        if (this.#globalSelectEvents) this.#globalSelectEvents.removeListeners()
        if (this.#selectionLogicEvents) this.#selectionLogicEvents.removeListeners()
        if (this.#tableRefresh) this.#tableRefresh.destroy()
        this.#scrollLock.destroy()

        if (this.#squinter !== null) this.#squinter.destroy()
        if (this.#fontSizeUnsubscribe) this.#fontSizeUnsubscribe()
        if (this.#easyFontUnsubscribe) this.#easyFontUnsubscribe()
        if (this.#layoutResizeObserver) this.#layoutResizeObserver.disconnect()
        if (this.#highlightSubtree) this.#highlightSubtree.destroy()
        if (this.#selectionLogic) this.#selectionLogic.destroy()
        if (this.#selectionCanvas) this.#selectionCanvas.destroy()
    }
}
